import React, { useState } from 'react';
import { Paper, Typography, Stack, Button, Tooltip } from '@mui/material';
import {
    Edit as EditIcon,
    AttachMoney,
    PictureAsPdf,
    CancelOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useUpdateOrderStatus } from '../../../hooks/orders/useUpdateOrderStatus';
import UpdateStatusDialog from './UpdateStatusDialog';
import RefundModal from './RefundModal';
import InvoiceViewerDialog from './InvoiceViewerDialog';

const REFUND_SUPPORTED_METHODS = new Set(['cod', 'paymob', 'manual']);
const TERMINAL_STATUSES = new Set(['cancelled', 'refunded', 'delivered']);

const OrderActionsPanel = ({ order }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [refundModalOpen, setRefundModalOpen] = useState(false);
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const { updateOrderStatusMutate, isUpdatingStatus } = useUpdateOrderStatus();

    const lastPayment = Array.isArray(order?.payments) && order.payments.length
        ? order.payments[order.payments.length - 1]
        : null;
    const refundable =
        lastPayment &&
        REFUND_SUPPORTED_METHODS.has(lastPayment.provider || lastPayment.method) &&
        ['paid', 'partially_refunded'].includes(lastPayment.status);

    const cancellable = !TERMINAL_STATUSES.has(order?.status) && order?.status !== 'shipped';
    const statusUpdatable = !TERMINAL_STATUSES.has(order?.status);

    const handleCancel = () => {
        const ok = window.confirm(
            isArabic ? 'هل أنت متأكد من إلغاء هذا الطلب؟' : 'Are you sure you want to cancel this order?'
        );
        if (!ok) return;
        updateOrderStatusMutate({
            id: order.id,
            data: { status: 'cancelled', comment: 'Cancelled by admin' },
        });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                position: { md: 'sticky' },
                top: { md: 16 },
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {isArabic ? 'الإجراءات' : 'Actions'}
            </Typography>

            <Stack spacing={1.25}>
                {statusUpdatable && (
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => setStatusDialogOpen(true)}
                        disabled={isUpdatingStatus}
                        fullWidth
                        sx={{ borderRadius: 2, py: 1.1, fontWeight: 'bold' }}
                    >
                        {isArabic ? 'تحديث الحالة' : 'Update Status'}
                    </Button>
                )}

                {order?.status === 'delivered' && (
                    <Typography
                        variant="body2"
                        sx={{
                            p: 1.25,
                            borderRadius: 2,
                            bgcolor: (t) => t.palette.action.hover,
                            color: 'text.secondary',
                            textAlign: 'center',
                        }}
                    >
                        {isArabic
                            ? 'تم تسليم الطلب. الإجراء التالي (الارتجاع) يتم من قِبَل العميل فقط.'
                            : 'Order delivered. The next action (return) can only be initiated by the customer.'}
                    </Typography>
                )}

                <Tooltip
                    title={
                        !refundable
                            ? (isArabic
                                ? 'الاسترداد غير مدعوم لطريقة الدفع هذه'
                                : 'Refund not supported for this payment method')
                            : ''
                    }
                >
                    <span>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<AttachMoney />}
                            disabled={!refundable}
                            fullWidth
                            sx={{ borderRadius: 2, py: 1.1, fontWeight: 'bold' }}
                            onClick={() => setRefundModalOpen(true)}
                        >
                            {isArabic ? 'استرداد' : 'Refund'}
                        </Button>
                    </span>
                </Tooltip>

                <Button
                    variant="outlined"
                    startIcon={<PictureAsPdf />}
                    fullWidth
                    onClick={() => setInvoiceOpen(true)}
                    sx={{ borderRadius: 2, py: 1.1, fontWeight: 'bold' }}
                >
                    {isArabic ? 'عرض / تحميل الفاتورة' : 'View / Download Invoice'}
                </Button>

                {cancellable && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CancelOutlined />}
                        onClick={handleCancel}
                        disabled={isUpdatingStatus}
                        fullWidth
                        sx={{ borderRadius: 2, py: 1.1, fontWeight: 'bold' }}
                    >
                        {isArabic ? 'إلغاء الطلب' : 'Cancel Order'}
                    </Button>
                )}
            </Stack>

            <UpdateStatusDialog
                open={statusDialogOpen}
                order={order}
                onClose={() => setStatusDialogOpen(false)}
            />

            <RefundModal
                open={refundModalOpen}
                order={order}
                onClose={() => setRefundModalOpen(false)}
            />

            <InvoiceViewerDialog
                open={invoiceOpen}
                order={order}
                onClose={() => setInvoiceOpen(false)}
            />
        </Paper>
    );
};

export default OrderActionsPanel;
